<template>
	<div data-tour-scope="stats">
		<v-main>
			<v-container fluid class="stats-dashboard px-4">
				<div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
					<div>
						<h2 class="text-h6 font-weight-medium">Visite del sito</h2>
						<div class="text-caption grey--text">Traffico del sito pubblico da Google Analytics, confrontato con il periodo scelto</div>
					</div>
					<v-btn class="ml-6" outlined small color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left small>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
					<v-spacer />
				</div>

				<PeriodBar
					v-model="filters"
					data-tour="period"
					:partial="!!(overview && overview.period.partial)"
					:loading="loading"
					:show-platform="false"
					:show-report="false"
					class="mb-3"
					@refresh="aggiorna"
				/>

				<v-tabs v-model="tab" class="mb-3" background-color="transparent" data-tour="tabs">
					<v-tab>Visite</v-tab>
					<v-tab>Comportamento</v-tab>
				</v-tabs>

				<ClarityBehavior v-if="tab === 1" :filters="filters" :refresh-key="refreshKey" />

				<template v-else>
					<v-alert v-if="nonConfigurato" type="info" outlined dense class="mb-3">
						Google Analytics non è collegato a questo negozio: attiva il servizio Google Analytics nelle impostazioni dei servizi (ID proprietà e credenziali) per vedere le visite del sito.
					</v-alert>
					<v-alert v-else-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

					<template v-if="overview">
						<div class="text-caption grey--text mb-2">
							{{ periodoLeggibile(overview.period.current) }} · confronto con {{ periodoLeggibile(overview.period.previous) }}
							<span v-if="overview.period.partial"> (a parità di giorni)</span>
						</div>

						<AnalyticsKpis :kpis="overview.kpis" :previous="overview.previous" :deltas="overview.deltas" :trend="overview.trend" class="mb-1" />

						<v-row dense>
							<v-col cols="12" lg="8">
								<VisitsTrend :trend="overview.trend" :compare="overview.period.compare" />
							</v-col>
							<v-col cols="12" lg="4">
								<SourcesPanel :channels="overview.channels" :sources="overview.sources" />
							</v-col>
						</v-row>

						<v-row dense>
							<v-col cols="12">
								<PagesTable :pages="overview.pages" />
							</v-col>
						</v-row>

						<v-row dense>
							<v-col cols="12" md="5" lg="4">
								<DevicesPanel :devices="overview.devices" />
							</v-col>
							<v-col cols="12" md="7" lg="8">
								<v-card outlined height="100%" data-tour="countries">
									<v-card-title class="text-subtitle-1 font-weight-medium pb-1">
										Paesi dei visitatori
										<v-spacer />
										<span class="text-caption grey--text">primi 10 per visitatori</span>
									</v-card-title>
									<v-card-text>
										<ShareList :rows="paesi" color="#8E24AA" />
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</template>
					<v-skeleton-loader v-else-if="loading" type="card-heading, image, table" />
				</template>
			</v-container>
		</v-main>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import PeriodBar from '@/components/dashboard/PeriodBar.vue';
import { filtersFromQuery, filtersToParams } from '@/components/dashboard/filters';
import AnalyticsKpis from '@/components/stats/AnalyticsKpis.vue';
import ShareList from '@/components/stats/ShareList.vue';
import { nomePaese, bandiera } from '@/components/stats/format';
import { createStatsTourSteps } from './stats-tour';

const VisitsTrend = () => import('@/components/stats/VisitsTrend.vue');
const SourcesPanel = () => import('@/components/stats/SourcesPanel.vue');
const PagesTable = () => import('@/components/stats/PagesTable.vue');
const DevicesPanel = () => import('@/components/stats/DevicesPanel.vue');
const ClarityBehavior = () => import('@/components/stats/ClarityBehavior.vue');

// schede della pagina: la scelta resta nell'URL (?tab=comportamento)
const TABS = ['visite', 'comportamento'];

// Statistiche = visite del sito pubblico (Google Analytics), impostata come la dashboard vendite
export default {
	name: 'Stats',
	components: { TourOverlay, PeriodBar, AnalyticsKpis, ShareList, VisitsTrend, SourcesPanel, PagesTable, DevicesPanel, ClarityBehavior },
	setup() {
		return { tour: useTour(createStatsTourSteps()) };
	},
	data() {
		return {
			filters: filtersFromQuery(this.$route.query),
			overview: null,
			loading: false,
			error: '',
			nonConfigurato: false,
			tab: Math.max(TABS.indexOf(this.$route.query.tab), 0),
			// cambia a ogni "Aggiorna": la scheda Comportamento ricarica i suoi dati
			refreshKey: 0,
		};
	},
	computed: {
		paesi() {
			return ((this.overview && this.overview.countries) || []).map(c => ({ ...c, label: nomePaese(c.key, c.name), flag: bandiera(c.key) }));
		},
	},
	watch: {
		filters(value, old) {
			const params = filtersToParams(value);
			if (JSON.stringify(params) === JSON.stringify(filtersToParams(old))) return;
			this.$router.replace({ query: this.query(params) }).catch(() => {});
			this.load();
		},
		tab() {
			this.$router.replace({ query: this.query(filtersToParams(this.filters)) }).catch(() => {});
		},
	},
	created() {
		this.load();
	},
	beforeDestroy() {
		this.tour.stop();
	},
	methods: {
		...mapActions({ fetchOverview: 'stats/asyncFetchOverview' }),
		query(params) {
			return this.tab ? { ...params, tab: TABS[this.tab] } : params;
		},
		aggiorna() {
			if (this.tab === 1) this.refreshKey += 1;
			else this.load();
		},
		async load() {
			if (this.filters.period === 'custom' && (!this.filters.from || !this.filters.to)) return;
			// niente canale: le visite del sito non sono divise per canale di vendita
			const params = filtersToParams({ ...this.filters, platform: '' });
			this.loading = true;
			this.error = '';
			this.nonConfigurato = false;
			try {
				this.overview = await this.fetchOverview(params);
			} catch (e) {
				const data = (e.response && e.response.data) || {};
				if (data.message === 'analytics_not_configured') this.nonConfigurato = true;
				else this.error = (data.error && data.error[0] && data.error[0].msg) || 'Impossibile caricare le statistiche. Riprova tra qualche istante.';
				this.overview = null;
			} finally {
				this.loading = false;
			}
		},
		// { startDate, endDate } -> "1 set 2026 – 30 set 2026"
		periodoLeggibile(r) {
			if (!r) return '';
			const f = d => new Date(`${d}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
			return r.startDate === r.endDate ? f(r.startDate) : `${f(r.startDate)} – ${f(r.endDate)}`;
		},
	},
};
</script>

<style scoped>
.stats-dashboard {
	max-width: 1600px;
}
</style>
